// App Builder → Surfaces → "AI clients (MCP)": the hosted instance is an MCP
// server (kit api/mcpServer.js, since kit-2026.10.13-mcp). This card gives
// the URL, the client snippets, the monitor's verdict, and a single-sign-on
// jump to the instance's Team page where personal access tokens are minted
// and OAuth grants are listed. Nothing here talks to the instance directly
// — the fleet monitor (api/monitorWorker.js) already probes it.
import React, { useState } from 'react';
import { DevConsoleButton } from '../pages/org/AppsModule';

function Snippet({ label, text }) {
  const [done, setDone] = useState(false);
  const copy = () => navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); });
  return (
    <div>
      <div className="u-cluster" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="cs-help u-small" style={{ margin: 0 }}>{label}</span>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={copy}>{done ? 'Copied' : 'Copy'}</button>
      </div>
      <code className="cs-code" style={{ fontSize: 'var(--fs-xs)' }}>{text}</code>
    </div>
  );
}

export default function McpClients({ org, app, instance }) {
  if (!instance?.urls?.app) {
    return <p className="cs-help">Link or provision a hosted instance first — every instance ships with its own MCP server.</p>;
  }
  const url = `${String(instance.urls.app).replace(/\/+$/, '')}/api/mcp`;
  const key = String(app?.name || instance.slug || 'app').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'app';
  const mcp = instance.monitor?.mcp;
  const tone = mcp === 'up' ? 'is-ok' : mcp === 'down' ? 'is-err' : mcp === 'unsupported' ? 'is-warn' : 'is-muted';
  const verdict = mcp === 'up' ? 'ready' : mcp === 'down' ? 'not answering' : mcp === 'unsupported' ? 'needs a kit update (reprovision)' : 'not probed yet';
  const cursor = JSON.stringify({ mcpServers: { [key]: { url, headers: { Authorization: 'Bearer <token from the instance Team page>' } } } }, null, 2);
  const claudeCode = `claude mcp add --transport http ${key} ${url} --header "Authorization: Bearer <token>"`;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p className="cs-help" style={{ margin: 0 }}>
        Cursor, Claude Code, Claude Desktop, claude.ai connectors and ChatGPT can work this app directly: the same record tools and knowledge the in-app assistant has, under each member's own seat, every call audited on the instance.
        {' '}<span className={`ap-badge ${tone}`} title={instance.monitor?.mcpError || ''}>{verdict}</span>
      </p>
      <Snippet label="MCP server URL — OAuth clients (Claude Desktop, claude.ai, ChatGPT) need only this; members sign in with their instance account" text={url} />
      <details>
        <summary className="cs-help" style={{ cursor: 'pointer', margin: 0 }}>Token-based setup (Cursor · Claude Code)</summary>
        <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
          <Snippet label="Cursor — .cursor/mcp.json" text={cursor} />
          <Snippet label="Claude Code" text={claudeCode} />
        </div>
      </details>
      <div className="u-cluster" style={{ gap: 8 }}>
        {instance.urls?.dev && (
          <DevConsoleButton orgId={org.id} appId={instance.id} devUrl={`${String(instance.urls.app).replace(/\/+$/, '')}/team`} next="/team" label="Create a token on the instance" />
        )}
        <span className="cs-help" style={{ margin: 0 }}>Tokens and OAuth grants live on the instance (Team › AI clients) — scoped read or read + write, revocable any time.</span>
      </div>
    </div>
  );
}
